import { GripVertical, Paperclip } from 'lucide-react';
import { useEffect, useId, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { boardGroups, boardTotals, type BoardColumn, type BoardItem } from '@/lib/kanban';
import { Badge } from './Badge';
import { Deadline } from './Deadline';
import { EmptyState } from './EmptyState';
import { Money } from './Money';
import { cn } from './cn';

export type { BoardColumn, BoardItem } from '@/lib/kanban';
interface Move {
  id: string;
  target: string;
  keyboard: boolean;
}
const MIME = 'application/x-kaname-card';

/** Controlled board. The owner commits each move and provides undo. */
export function KanbanBoard({
  columns,
  items,
  onMove,
  onOpen,
  now,
  label = 'Доска по статусам',
}: {
  columns: BoardColumn[];
  items: BoardItem[];
  onMove: (id: string, columnId: string) => void;
  onOpen?: (item: BoardItem, trigger: HTMLElement) => void;
  now?: number;
  label?: string;
}) {
  const boardId = useId();
  const help = useId();
  const [move, setMove] = useState<Move | null>(null);
  const session = useRef<Move | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const handles = useRef(new Map<string, HTMLButtonElement>());
  const destinations = useRef(new Map<string, HTMLElement>());
  const viewport = useRef<HTMLDivElement>(null);
  const groups = boardGroups(columns, items);
  useEffect(() => {
    const current = session.current;
    if (
      current &&
      (!items.some((item) => item.id === current.id) ||
        !columns.some((column) => column.id === current.target))
    ) {
      session.current = null;
      setMove(null);
      setAnnouncement('Перенос отменён: карточка или колонка больше недоступна');
    }
  }, [columns, items]);
  const select = (next: Move | null) => {
    session.current = next;
    setMove(next);
  };
  const end = (targetId?: string, returnFocus = true) => {
    const current = session.current;
    if (!current) return;
    select(null);
    const item = items.find((entry) => entry.id === current.id);
    const target = columns.find((column) => column.id === targetId);
    if (item && target && target.id !== item.columnId) {
      onMove(item.id, target.id);
      setAnnouncement(item.title + ': ' + target.title);
    } else setAnnouncement('Перенос отменён');
    if (returnFocus)
      requestAnimationFrame(() =>
        (handles.current.get(current.id) ?? viewport.current)?.focus({ preventScroll: true }),
      );
  };
  const begin = (item: BoardItem, keyboard: boolean) => {
    if (!columns.length) return;
    select({
      id: item.id,
      target: columns.some((column) => column.id === item.columnId) ? item.columnId : columns[0].id,
      keyboard,
    });
    setAnnouncement(
      'Взята карточка ' + item.title + '. Выбери колонку стрелками, подтверди Enter или отмени Escape.',
    );
  };
  const key = (event: KeyboardEvent, item: BoardItem) => {
    const current = session.current;
    if (!current && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      event.stopPropagation();
      begin(item, true);
      return;
    }
    if (current?.id !== item.id || !current.keyboard) return;
    if (event.key === 'Tab') {
      end(undefined, false);
      return;
    }
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.key === 'Escape') end();
    else if (event.key === 'Enter' || event.key === ' ') end(current.target);
    else if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key) && columns.length) {
      const index = Math.max(
        0,
        columns.findIndex((column) => column.id === current.target),
      );
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? columns.length - 1
            : Math.min(columns.length - 1, Math.max(0, index + (event.key === 'ArrowRight' ? 1 : -1)));
      const target = columns[next];
      select({ ...current, target: target.id });
      setAnnouncement('Колонка ' + target.title);
      destinations.current
        .get(target.id)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }
  };
  const validDrag = (event: DragEvent) => {
    const current = session.current;
    return (
      current &&
      !current.keyboard &&
      event.dataTransfer.types.includes(MIME) &&
      items.some((item) => item.id === current.id)
    );
  };
  const card = (item: BoardItem) => (
    <article
      key={item.id}
      role="listitem"
      data-board-item={item.id}
      data-moving={move?.id === item.id || undefined}
      className={cn(
        'rounded-md border border-line bg-card p-4 shadow-xs',
        move?.id === item.id && 'ring-2 ring-ring',
      )}
    >
      <div className="mb-4 flex items-start gap-2">
        {onOpen ? (
          <button
            type="button"
            onClick={(event) => onOpen(item, event.currentTarget)}
            className="min-w-0 flex-1 rounded-xs text-left text-sm font-semibold break-words text-fg hover:text-brand-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            {item.title}
          </button>
        ) : (
          <h4 className="min-w-0 flex-1 text-sm font-semibold break-words text-fg">{item.title}</h4>
        )}
        <button
          type="button"
          data-board-handle={item.id}
          ref={(element) => {
            if (element) handles.current.set(item.id, element);
            else handles.current.delete(item.id);
          }}
          disabled={!columns.length}
          draggable={columns.length > 0}
          title="Перенести: пробел, стрелки, Enter"
          aria-label={'Переместить ' + item.title}
          aria-describedby={help}
          aria-pressed={move?.id === item.id}
          onKeyDown={(event) => key(event, item)}
          onBlur={() => {
            if (session.current?.id === item.id && session.current.keyboard) end(undefined, false);
          }}
          onDragStart={(event) => {
            event.stopPropagation();
            event.dataTransfer.setData(MIME, JSON.stringify({ boardId, itemId: item.id }));
            event.dataTransfer.effectAllowed = 'move';
            const article = event.currentTarget.closest('article');
            if (article) event.dataTransfer.setDragImage(article, 24, 24);
            begin(item, false);
          }}
          onDragEnd={() => end()}
          className="flex size-6 shrink-0 cursor-grab items-center justify-center rounded-xs text-fg-muted hover:bg-hover hover:text-fg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none active:cursor-grabbing disabled:cursor-default disabled:opacity-40"
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      </div>
      {(item.minorUnits !== undefined || item.currency !== undefined) && (
        <div className="text-sm">
          <Money minorUnits={item.minorUnits ?? NaN} currency={item.currency ?? ''} />
        </div>
      )}
      {(item.dueAt || Boolean(item.attachments && item.attachments > 0)) && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs">
          {item.dueAt && <Deadline at={item.dueAt} now={now} overdueTone="strong" />}
          {Number.isInteger(item.attachments) && item.attachments! > 0 && (
            <span
              className="inline-flex items-center gap-1 text-fg-muted"
              aria-label={'Вложений: ' + item.attachments}
            >
              <Paperclip className="size-3.5" aria-hidden />
              {item.attachments}
            </span>
          )}
        </div>
      )}
    </article>
  );
  return (
    <div data-slot="kanban-board" role="region" aria-label={label} className="min-w-0">
      <p id={help} className="sr-only">
        Пробел или Enter — взять карточку. Стрелки влево и вправо — выбрать колонку. Home и End — первая и
        последняя. Enter — перенести. Escape или Tab — отменить.
      </p>
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      {!columns.length && (
        <EmptyState
          title="Статусы не заданы"
          description="Добавь колонки, чтобы переносить карточки между статусами."
        />
      )}
      <div
        ref={viewport}
        data-scrollport=""
        tabIndex={0}
        aria-label="Колонки доски"
        className="overflow-x-auto rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <div className="flex min-w-full items-start gap-4 p-1 pb-3">
          {groups.columns.map(({ column, items: cards }) => {
            const totals = boardTotals(cards);
            const target = move?.target === column.id;
            return (
              <section
                key={column.id}
                data-board-column={column.id}
                aria-label={column.title}
                ref={(element) => {
                  if (element) destinations.current.set(column.id, element);
                  else destinations.current.delete(column.id);
                }}
                className={cn(
                  'min-h-80 w-80 min-w-72 flex-1 rounded-lg border bg-muted p-3 transition-colors duration-fast',
                  target ? 'border-brand' : 'border-line',
                )}
                onDragOver={(event) => {
                  if (validDrag(event)) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    if (!target) select({ ...session.current!, target: column.id });
                  }
                }}
                onDrop={(event) => {
                  if (
                    !validDrag(event) ||
                    event.dataTransfer.getData(MIME) !==
                      JSON.stringify({ boardId, itemId: session.current!.id })
                  )
                    return;
                  event.preventDefault();
                  event.stopPropagation();
                  end(column.id);
                }}
              >
                <header className="mb-4 flex flex-col gap-2 px-1 pt-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="type-subheading">
                      <Badge solid variant={column.tone ?? 'neutral'}>
                        {column.title}
                      </Badge>
                    </h3>
                    <span
                      data-board-count=""
                      className="text-xs text-fg-muted tabular-nums"
                      aria-label={'Карточек: ' + cards.length}
                    >
                      {cards.length}
                    </span>
                  </div>
                  <div
                    data-board-total=""
                    className="flex min-h-4 flex-wrap gap-x-3 gap-y-1 text-xs text-fg-muted"
                  >
                    {totals.totals.length
                      ? totals.totals.map((total) =>
                          total.minorUnits === null ? (
                            <span key={total.currency}>Сумма {total.currency} недоступна</span>
                          ) : (
                            <Money
                              key={total.currency}
                              currency={total.currency}
                              minorUnits={total.minorUnits}
                            />
                          ),
                        )
                      : 'Нет сумм'}
                    {totals.incomplete && <span>Есть суммы без валюты</span>}
                  </div>
                </header>
                <div role="list" aria-label={'Карточки: ' + column.title} className="flex flex-col gap-3">
                  {cards.map(card)}
                </div>
                {move && target ? (
                  <div
                    data-board-destination=""
                    className="mt-3 rounded-md border border-dashed border-brand bg-brand-soft p-4 text-xs text-brand-fg"
                  >
                    Место для карточки
                  </div>
                ) : (
                  !cards.length && <p className="px-1 py-4 text-xs text-fg-muted">Пока нет карточек</p>
                )}
              </section>
            );
          })}
          {groups.unassigned.length > 0 && (
            <section
              data-board-unassigned=""
              aria-label="Без статуса"
              className="min-h-80 w-80 min-w-72 flex-1 rounded-lg border border-line bg-muted p-3"
            >
              <header className="mb-4 px-1 pt-1">
                <h3 className="type-subheading">Без статуса · {groups.unassigned.length}</h3>
                <p className="mt-2 text-xs text-fg-muted">Статус недоступен. Перенеси в одну из колонок.</p>
              </header>
              <div role="list" aria-label="Карточки без статуса" className="flex flex-col gap-3">
                {groups.unassigned.map(card)}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
